import { CSSProperties, useEffect, useRef, useState } from "react";
import { cancelOrder, errorText, placeAutoLadder } from "./api";
import { LADDER_STEPS, useLadderSettings } from "./ladderSettings";
import { useIbkrData, useVisible } from "./poller";
import { OrderInfo, QuoteRow } from "./types";
import { C, ContractTitle, PHASE_DOT, PHASE_LABEL, StatusDot, SizeField, StepChoice, UnderlyingQuote, WidgetBox, WindowPhase, fmt, fmtTotal, isWorking } from "./ui";
import { WidgetProps, referencePrice } from "./windows";

// two 96px side buttons + their 8px gap: the bid / last / ask row sits right above them
const SIDE_BUTTONS_WIDTH = 200;

// Ladder window: the backend's auto ladder steps a limit order from the near
// side of the spread towards the far side, re-anchored to the current bid / ask
// every step, until it fills; step size and seconds per step come from the
// chat's ladder settings when it starts. params {conId, size, side?}. Every ladder window
// has a size stepper and Buy / Sell buttons; the side the user asked for
// ("ladder buy ...") or, once started, the chosen side (lside) is shown as the
// BUY / SELL tag in the header. Once started, orderId and lside are stored in params.
export function LadderWidget({ widget, actions }: WidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useVisible(ref);
  const p = widget.params;
  const orderId: number | undefined = p.orderId;
  const preset: "BUY" | "SELL" | null = p.side ?? null;

  // an option's underlying is polled along (its conId is stored once known, so the request stays stable)
  const quoteData = useIbkrData<QuoteRow[]>("quotes", { conIds: p.underConId ? [p.conId, p.underConId] : [p.conId] }, visible);
  const row = quoteData.data?.[0];
  const underRow = p.underConId ? quoteData.data?.[1] : undefined;
  const quote = row && "quote" in row ? row.quote : undefined;
  const contract = row && "contract" in row ? row.contract : undefined;
  const knownUnderConId = contract?.underConId;
  useEffect(() => {
    if (knownUnderConId && !p.underConId) actions.patch({ underConId: knownUnderConId });
  }, [knownUnderConId, p.underConId, actions]);
  const orderData = useIbkrData<OrderInfo>("order", { orderId: orderId ?? -1, permId: p.permId }, visible && orderId != null);
  // the permId identifies the order at IB even after a backend reconnect
  const knownPermId = orderData.data?.permId;
  useEffect(() => {
    if (knownPermId && !p.permId) actions.patch({ permId: knownPermId });
  }, [knownPermId, p.permId, actions]);
  const order = orderId != null ? orderData.data : undefined;

  const [busy, setBusy] = useState(false);
  // in the params, not component state: the chat snapshot sent to the agent includes them
  const error: string | null = p.error ?? null;
  const setError = (e: string | null) => actions.patch({ error: e ?? undefined });

  const phase: WindowPhase = orderId == null ? "preview" : order?.status ?? "pending";
  // the backend doesn't know the order (any more): nothing can be done with it
  const lost = orderId != null && !orderData.data && !!orderData.error;
  const working = isWorking(phase) && !lost;
  const lside: "BUY" | "SELL" | null = p.lside ?? null;
  const size: number = p.size;
  // price move per ladder step, picked in this window
  const step: number = LADDER_STEPS.includes(p.step) ? p.step : LADDER_STEPS[0];

  const start = async (s: "BUY" | "SELL") => {
    setBusy(true);
    try {
      const { stepDuration } = useLadderSettings.getState();
      const placed = await placeAutoLadder(p.conId, s === "BUY" ? size : -size, step, stepDuration);
      actions.patch({ orderId: placed.orderId, permId: placed.permId || undefined, lside: s });
      setError(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const stop = async () => {
    if (orderId == null) return;
    setBusy(true);
    try {
      await cancelOrder(orderId);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  // estimated total before the ladder starts: the quote's reference price (last
  // within the bid-ask, otherwise the mid) × size × multiplier (100 for options)
  const estPrice = referencePrice(quote);
  const estimate = estPrice != null && contract ? estPrice * size * contract.multiplier : null;
  // steps from the near to the far side of the current spread, and how long they take
  const { stepDuration } = useLadderSettings();
  const spread = quote?.bid != null && quote?.ask != null ? quote.ask - quote.bid : null;
  const steps = spread != null && spread > 0 ? Math.ceil(Math.round((spread / step) * 1e6) / 1e6) : null;
  const stepsNote = steps != null ? ` (${steps}st | ${+(steps * stepDuration).toFixed(1)}s)` : "";

  const filled = order?.filled ?? 0;
  const caption = error ?? (phase === "rejected" ? order?.message : null) ?? orderData.error;
  const captionNode = caption
    ? <span style={{ color: C.cancelled }}>{caption}</span>
    : phase === "preview" ? (estimate != null ? `Est. ${fmtTotal(estimate, 0)}${stepsNote}` : "") : `Filled ${filled} / ${size}${filled > 0 && order?.avgFillPrice ? ` at ${fmt(order.avgFillPrice)}` : ""}`
      // while the ladder works, the price the order currently rests at
      + (working && order?.price != null ? ` · order @ ${fmt(order.price)}` : "");
  const status = <StatusDot color={PHASE_DOT[phase]} label={PHASE_LABEL[phase]} />;
  const duplicate = () => actions.duplicate({ conId: p.conId, size, side: preset, step });
  const doneLabel = PHASE_LABEL[phase][0].toUpperCase() + PHASE_LABEL[phase].slice(1);

  // one button for side `me`: label, colors, action per phase
  const sideButton = (me: "BUY" | "SELL", width: number, height: number, label: string) => {
    const base = me === "BUY" ? C.buy : C.sell;
    const style: CSSProperties = { flex: "none", width, boxSizing: "border-box", height, padding: "0 8px", whiteSpace: "nowrap", fontSize: 13, fontWeight: 500, border: 0, color: "#fff" };
    if (phase === "preview") return <button disabled={busy} onClick={() => start(me)} style={{ ...style, background: base }}>{label}</button>;
    if (lside !== me) return <button disabled style={{ ...style, background: C.disabled }}>{label}</button>;
    if (working) {
      // a working buy turns orange "Cancel", a working sell turns white "Cancel"
      const cancelStyle: CSSProperties = me === "BUY" ? { background: C.sell } : { background: "#fff", color: C.text, border: `1px solid ${C.border}` };
      return <button disabled={busy} onClick={stop} style={{ ...style, ...cancelStyle }}>Cancel</button>;
    }
    return <button disabled style={{ ...style, background: C.done }}>{doneLabel}</button>;
  };

  const prices = (fontSize: number) =>
    (["bid", "last", "ask"] as const).map((k) => (
      <span key={k} style={{ flex: "none", display: "flex", alignItems: "center", fontSize, fontWeight: k === "last" ? 600 : 400 }}>{fmt(quote?.[k])}</span>
    ));

  return (
    <WidgetBox boxRef={ref} title={<ContractTitle contract={contract} side={lside ?? preset ?? undefined} />} status={status} onDuplicate={duplicate} onClose={actions.close}>
      <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* the ladder's step size above the size, bid / last / ask above the Buy / Sell buttons */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <StepChoice options={LADDER_STEPS} value={step} onChange={(v) => actions.patch({ step: v })} disabled={phase !== "preview" || busy} />
          </div>
          <div style={{ flex: "none", width: SIDE_BUTTONS_WIDTH, display: "flex", justifyContent: "space-around" }}>{prices(16)}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SizeField big={false} size={size} disabled={phase !== "preview" || busy} onChange={(v) => actions.patch({ size: v })}
            note={(v) => (estPrice != null && contract ? fmtTotal(estPrice * v * contract.multiplier, 0) : null)} />
          {sideButton("BUY", 96, 36, "Buy")}
          {sideButton("SELL", 96, 36, "Sell")}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, minHeight: 16 }}>
          <UnderlyingQuote row={underRow} />
          <span style={{ fontSize: 11.5, color: C.muted, textAlign: caption ? "left" : "right", minWidth: 0, overflowWrap: "anywhere" }}>{captionNode}</span>
        </div>
      </div>
    </WidgetBox>
  );
}

